// GtSubmissions — the commercial surface for a role. Every candidate presented to
// the client, what the client said, what it is worth, and how long we own it.
import { useState, useEffect, useCallback } from 'react';
import GtLoader from '../../components/GtLoader';
import { GtSelect } from '../../components/GtControls';

const CURRENCIES = [
  { value: 'GBP', label: 'GBP £' }, { value: 'USD', label: 'USD $' },
  { value: 'EUR', label: 'EUR €' }, { value: 'CAD', label: 'CAD $' },
  { value: 'AUD', label: 'AUD $' },
];
const SYMBOL = { GBP: '£', USD: '$', EUR: '€', CAD: 'C$', AUD: 'A$' };
const BASES = [
  { value: 'hour', label: 'per hour' }, { value: 'day', label: 'per day' },
  { value: 'week', label: 'per week' }, { value: 'month', label: 'per month' },
  { value: 'year', label: 'per year' },
];
const FEE_MODELS = [{ value: 'fixed', label: 'Fixed amount' }, { value: 'percent', label: 'Percentage of rate' }];
const STATUS = [
  { value: 'in_progress', label: 'In progress' }, { value: 'placed', label: 'Placed' },
  { value: 'rejected', label: 'Rejected' }, { value: 'withdrawn', label: 'Withdrawn' },
];

const STATUS_PILL = { in_progress: 'gtt-p-blue', placed: 'gtt-p-green', rejected: 'gtt-p-rose', withdrawn: 'gtt-p-slate' };
const OUTCOME_LABEL = { submitted: 'Submitted', viewed: 'Viewed', interview_requested: 'Client interested', placed: 'Placed', rejected: 'Declined', withdrawn: 'Withdrawn' };
const OUTCOME_TONE = { interview_requested: 'up', placed: 'up', rejected: 'down', withdrawn: 'down' };

// Annualise a rate so a percentage fee means something on any contract shape.
const PER_YEAR = { hour: 1800, day: 225, week: 46, month: 12, year: 1 };
function annualise(rate, basis, months) {
  const r = Number(rate); if (!r || !basis) return null;
  const mult = PER_YEAR[basis]; if (!mult) return null;
  const annual = r * mult;
  if (basis !== 'year' && months) return annual * (Math.min(Number(months), 12) / 12);
  return annual;
}
function computedFee(s) {
  if (s.fee_model === 'percent') {
    const base = annualise(s.agreed_rate_gbp, s.agreed_rate_basis, s.contract_months);
    if (!base || !s.fee_percent) return null;
    return base * (Number(s.fee_percent) / 100);
  }
  return s.placement_fee_gbp !== null && s.placement_fee_gbp !== undefined && s.placement_fee_gbp !== ''
    ? Number(s.placement_fee_gbp) : null;
}
function fmt(n, cur) {
  if (n === null || n === undefined || isNaN(n)) return null;
  return (SYMBOL[cur] || '£') + Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 });
}
function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - Date.now()) / 86400000);
}
function ukDate(v) {
  if (!v) return '—';
  const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }

export default function GtSubmissions({ roleId, clientName, authFetch, flash, onOpenCandidate }) {
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await authFetch('/api/roles/' + roleId + '/submissions'); setSubs(r.submissions || []); }
    catch (e) { setSubs([]); }
    setLoading(false);
  }, [authFetch, roleId]);

  useEffect(() => { load(); }, [load]);

  function startEdit(s) {
    setForm({
      currency: s.currency || 'GBP',
      fee_model: s.fee_model || 'fixed',
      placement_fee_gbp: s.placement_fee_gbp ?? '',
      fee_percent: s.fee_percent ?? '',
      agreed_rate_gbp: s.agreed_rate_gbp ?? '',
      agreed_rate_basis: s.agreed_rate_basis || 'year',
      contract_months: s.contract_months ?? '',
      placement_status: s.placement_status || 'in_progress',
    });
    setEditId(s.id);
  }

  async function save(id) {
    setSaving(true);
    try {
      const body = { ...form };
      ['placement_fee_gbp', 'fee_percent', 'agreed_rate_gbp', 'contract_months'].forEach(k => {
        body[k] = body[k] === '' || body[k] === null ? null : Number(body[k]);
      });
      await authFetch('/api/submissions/' + id, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      setEditId(null); await load(); flash('Commercials updated');
    } catch (e) { flash(e.message); }
    setSaving(false);
  }

  if (subs === null) return <GtLoader compact />;

  if (subs.length === 0) {
    return (
      <div className="gtsb-empty">
        <div className="gtsb-empty-mark">◆</div>
        <h3>No candidates presented yet</h3>
        <p>Sharing a profile bundle with {clientName || 'the client'} submits those candidates, starts the ownership window, and tracks every response here.</p>
      </div>
    );
  }

  const ql = q.trim().toLowerCase();
  const matches = subs.filter(function (s) {
    if (ql) {
      var hay = [s.full_name, s.candidate_ref, s.current_company, s.headline].join(' ').toLowerCase();
      if (hay.indexOf(ql) === -1) return false;
    }
    if (verdictFilter === 'interested' && s.latest_event !== 'interview_requested') return false;
    if (verdictFilter === 'declined' && s.latest_event !== 'rejected') return false;
    if (verdictFilter === 'awaiting' && s.latest_event !== 'submitted') return false;
    if (verdictFilter === 'expiring') {
      var d = daysLeft(s.ownership_expires_at);
      if (d === null || d > 60) return false;
    }
    return true;
  }).sort(function (a, b) {
    if (sortBy === 'value') return (computedFee(b) || 0) - (computedFee(a) || 0);
    if (sortBy === 'expiring') return new Date(a.ownership_expires_at) - new Date(b.ownership_expires_at);
    if (sortBy === 'name') return String(a.full_name || '').localeCompare(String(b.full_name || ''));
    return new Date(b.submitted_at) - new Date(a.submitted_at);
  });

  const live = matches.filter(s => !['placed', 'rejected', 'withdrawn'].includes(s.placement_status));
  const placed = matches.filter(s => s.placement_status === 'placed');
  const closed = matches.filter(s => ['rejected', 'withdrawn'].includes(s.placement_status));
  const interested = subs.filter(s => s.latest_event === 'interview_requested').length;
  const feeTotal = placed.reduce((a, s) => a + (computedFee(s) || 0), 0);
  const pipelineValue = live.reduce((a, s) => a + (computedFee(s) || 0), 0);
  const mainCur = (placed[0] && placed[0].currency) || (subs[0] && subs[0].currency) || 'GBP';

  function row(s) {
    const editing = editId === s.id;
    const fee = computedFee(s);
    const dl = daysLeft(s.ownership_expires_at);
    const urgent = dl !== null && dl < 60;
    const tone = OUTCOME_TONE[s.latest_event];

    return (
      <div key={s.id} className={'gtsb-card' + (editing ? ' gtsb-card--editing' : '')}>
        <div className="gtsb-head">
          <span className="gtsb-av" style={{ background: avatarColour(s.full_name) }}>{initials(s.full_name)}</span>
          <div className="gtsb-who">
            <div className="gtsb-nameline">
              <button type="button" className="gtsb-name" onClick={() => onOpenCandidate && onOpenCandidate(s.candidate_ref || s.candidate_id)}>{s.full_name}</button>
              <span className="gtr-refbadge gtr-refbadge-sm">{s.candidate_ref}</span>
              <span className={'gtt-pill ' + (STATUS_PILL[s.placement_status] || 'gtt-p-slate')}>{(s.placement_status || 'in progress').replace('_', ' ')}</span>
            </div>
            <div className="gtsb-sub">
              {s.headline || s.current_company || '—'}
            </div>
          </div>
          {tone && (
            <span className={'gtsb-verdict gtsb-verdict--' + tone}>
              {tone === 'up'
                ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12M15 5.88L14 10h5.83a2 2 0 011.92 2.56l-2.33 8A2 2 0 0117.5 22H4a2 2 0 01-2-2v-8a2 2 0 012-2h2.76a2 2 0 001.79-1.11L12 2a3.13 3.13 0 013 3.88z" /></svg>
                : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3.13 3.13 0 01-3-3.88z" /></svg>}
              {OUTCOME_LABEL[s.latest_event]}
            </span>
          )}
        </div>

        <div className="gtsb-facts">
          <div className="gtsb-fact"><span>Submitted</span><b>{ukDate(s.submitted_at)}</b></div>
          <div className={'gtsb-fact' + (urgent ? ' gtsb-fact--urgent' : '')}>
            <span>Ownership</span>
            <b>{dl === null ? '—' : dl > 0 ? dl + ' days left' : 'expired'}</b>
          </div>
          <div className="gtsb-fact"><span>Stage</span><b className="gtsb-stage">{(s.stage || '—').replace('_', ' ')}</b></div>
          <div className="gtsb-fact"><span>Value</span><b className={fee ? 'gtsb-money' : ''}>{fmt(fee, s.currency) || 'not set'}</b></div>
        </div>

        {(s.shared_with || s.share_ref) && (
          <div className="gtsb-prov">Presented to {s.shared_with || 'a contact'}{s.share_ref ? ' · ' + s.share_ref : ''}</div>
        )}
        {s.rejected_reason && <div className="gtsb-reason">Client feedback: {s.rejected_reason}</div>}

        {Array.isArray(s.outcomes) && s.outcomes.length > 0 && (
          <div className="gtsb-trail">
            {s.outcomes.slice().reverse().map((o, i) => (
              <span key={i} className={'gtsb-trail-step' + (OUTCOME_TONE[o.event] ? ' gtsb-trail-step--' + OUTCOME_TONE[o.event] : '')}
                title={new Date(o.occurred_at).toLocaleString('en-GB')}>
                {OUTCOME_LABEL[o.event] || o.event}
              </span>
            ))}
          </div>
        )}

        {editing ? (
          <div className="gtsb-editor">
            <div className="gtsb-editrow">
              <label className="gtc-field"><span>Status</span>
                <GtSelect value={form.placement_status} options={STATUS} onChange={v => setForm({ ...form, placement_status: v })} /></label>
              <label className="gtc-field"><span>Currency</span>
                <GtSelect value={form.currency} options={CURRENCIES} onChange={v => setForm({ ...form, currency: v })} /></label>
              <label className="gtc-field"><span>Fee model</span>
                <GtSelect value={form.fee_model} options={FEE_MODELS} onChange={v => setForm({ ...form, fee_model: v })} /></label>
            </div>

            <div className="gtsb-editrow">
              <label className="gtc-field"><span>Agreed rate</span>
                <input value={form.agreed_rate_gbp} placeholder="e.g. 95000"
                  onChange={e => setForm({ ...form, agreed_rate_gbp: e.target.value.replace(/[^0-9.]/g, '') })} /></label>
              <label className="gtc-field"><span>Basis</span>
                <GtSelect value={form.agreed_rate_basis} options={BASES} onChange={v => setForm({ ...form, agreed_rate_basis: v })} /></label>
              {form.agreed_rate_basis !== 'year' && (
                <label className="gtc-field"><span>Contract months</span>
                  <input value={form.contract_months} placeholder="e.g. 6"
                    onChange={e => setForm({ ...form, contract_months: e.target.value.replace(/[^0-9.]/g, '') })} /></label>
              )}
            </div>

            <div className="gtsb-editrow">
              {form.fee_model === 'percent' ? (
                <label className="gtc-field"><span>Fee percentage</span>
                  <input value={form.fee_percent} placeholder="e.g. 18"
                    onChange={e => setForm({ ...form, fee_percent: e.target.value.replace(/[^0-9.]/g, '') })} /></label>
              ) : (
                <label className="gtc-field"><span>Fee amount</span>
                  <input value={form.placement_fee_gbp} placeholder="e.g. 12500"
                    onChange={e => setForm({ ...form, placement_fee_gbp: e.target.value.replace(/[^0-9.]/g, '') })} /></label>
              )}
              <div className="gtsb-preview">
                <span>Fee</span>
                <b>{fmt(computedFee({ ...form }), form.currency) || '—'}</b>
                {form.fee_model === 'percent' && form.agreed_rate_basis !== 'year' && (
                  <em>annualised from {form.contract_months || 12} months</em>
                )}
              </div>
            </div>

            <div className="gtsb-editacts">
              <button className="gtc-btn-ghost" onClick={() => setEditId(null)}>Cancel</button>
              <button className="gtc-primary" disabled={saving} onClick={() => save(s.id)}>{saving ? 'Saving…' : 'Save commercials'}</button>
            </div>
          </div>
        ) : (
          <div className="gtsb-acts">
            <button className="gtr-editbtn gtr-editbtn-ghost" onClick={() => onOpenCandidate && onOpenCandidate(s.candidate_ref || s.candidate_id)}>Open candidate</button>
            <button className="gtr-editbtn" onClick={() => startEdit(s)}>{fee ? 'Edit commercials' : 'Set commercials'}</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="gtsb">
      <div className="gtsb-strip">
        <div className="gtsb-stat"><span>Presented</span><b>{subs.length}</b></div>
        <div className="gtsb-stat gtsb-stat--teal"><span>Client interested</span><b>{interested}</b></div>
        <div className="gtsb-stat gtsb-stat--green"><span>Placed</span><b>{placed.length}</b></div>
        <div className="gtsb-stat gtsb-stat--money"><span>Fees earned</span><b>{fmt(feeTotal, mainCur) || (SYMBOL[mainCur] || '£') + '0'}</b></div>
        <div className="gtsb-stat"><span>In play</span><b>{fmt(pipelineValue, mainCur) || (SYMBOL[mainCur] || '£') + '0'}</b></div>
        <button className="gtsb-refresh" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <div className="gtsb-filters">
        <input className="gtsb-search" placeholder="Search by name, reference, company…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="gtsb-chips">
          {[['all','All'],['interested','Interested'],['awaiting','Awaiting reply'],['declined','Declined'],['expiring','Ownership expiring']].map(([k,label]) => (
            <button key={k} className={'gtsb-chip' + (verdictFilter === k ? ' gtsb-chip--on' : '')} onClick={() => setVerdictFilter(k)}>{label}</button>
          ))}
        </div>
        <div className="gtsb-sort">
          <GtSelect value={sortBy} options={[
            { value: 'recent', label: 'Newest first' },
            { value: 'value', label: 'Highest value' },
            { value: 'expiring', label: 'Ownership expiring' },
            { value: 'name', label: 'Name A-Z' },
          ]} onChange={setSortBy} />
        </div>
      </div>

      {matches.length === 0 && (
        <div className="gtr-emptytab">No submissions match this filter.</div>
      )}

      {live.length > 0 && (
        <section className="gtsb-group">
          <button className={'gtsb-grouphead'} onClick={() => setCollapsed(c => ({ ...c, live: !c.live }))}>
            <span className="gtsb-caret">{collapsed.live ? '\u203A' : '\u2304'}</span>Live <span>{live.length}</span>
          </button>
          {!collapsed.live && <div className="gtsb-list">{live.map(row)}</div>}
        </section>
      )}
      {placed.length > 0 && (
        <section className="gtsb-group">
          <button className={'gtsb-grouphead gtsb-grouphead--green'} onClick={() => setCollapsed(c => ({ ...c, placed: !c.placed }))}>
            <span className="gtsb-caret">{collapsed.placed ? '\u203A' : '\u2304'}</span>Placed <span>{placed.length}</span>
          </button>
          {!collapsed.placed && <div className="gtsb-list">{placed.map(row)}</div>}
        </section>
      )}
      {closed.length > 0 && (
        <section className="gtsb-group">
          <button className={'gtsb-grouphead gtsb-grouphead--muted'} onClick={() => setCollapsed(c => ({ ...c, closed: !c.closed }))}>
            <span className="gtsb-caret">{collapsed.closed ? '\u203A' : '\u2304'}</span>Closed <span>{closed.length}</span>
          </button>
          {!collapsed.closed && <div className="gtsb-list">{closed.map(row)}</div>}
        </section>
      )}
    </div>
  );
}
